import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { DateTimeFormatOptions } from 'luxon'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { publisher, releasedEvent, twitter } from '@/constants/seo'
import { Seo } from '@/layout/Seo'
import { ArticleJsonLd } from '@/layout/Seo/JsonLd'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { getPressRelease, PressRelease } from '@/services/PressService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getStaticPropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PressReleaseView } from '@/views/PressReleaseView'

interface PressReleaseProps {
  pressRelease: PressRelease
}

const PressReleasePage: NextPageWithLayout<PressReleaseProps> = ({ pressRelease }) => {
  const formatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  } as DateTimeFormatOptions

  const publishedDate = new Date(pressRelease.publishDate ?? pressRelease.sys.firstPublishedAt)
  const modifiedDate = new Date(pressRelease.sys.publishedAt)

  const formattedPublishDate = publishedDate.toLocaleDateString('en-US', formatOptions)
  const formattedModifiedDate = modifiedDate.toLocaleDateString('en-US', formatOptions)

  const url = useCanonicalUrl(`${paths.press.release}/${pressRelease.slug}`)
  const description =
    pressRelease.excerpt ?? `Angel Studios Press Release | ${formattedModifiedDate ?? formattedPublishDate}`

  const title = `${pressRelease.title ?? 'Press Release'} | Angel Studios`
  const openGraph = {
    title,
    description,
    url,
    type: 'article',
    article: {
      publishedTime: pressRelease.sys.firstPublishedAt,
      modifiedTime: pressRelease.sys.publishedAt,
      section: 'Media',
      authors: [twitter],
    },
    images: pressRelease.openGraphImage
      ? [
          {
            alt: title,
            height: 900,
            url: getCloudinaryImageUrl({
              height: 900,
              path: `${pressRelease.openGraphImage}`,
              width: 1600,
            }),
            width: 1600,
          },
        ]
      : [],
  }
  return (
    <>
      <ArticleJsonLd
        articleBody={documentToPlainTextString(pressRelease.body?.json)}
        dateModified={pressRelease.sys.publishedAt}
        datePublished={pressRelease.publishDate ?? pressRelease.sys.firstPublishedAt}
        description={description}
        headline={title}
        images={[pressRelease.openGraphImage]}
        publication={releasedEvent}
        publisher={publisher}
        url={url}
      />
      <Seo canonical={url} openGraph={openGraph} title={title} description={description} />
      <PressReleaseView pressRelease={pressRelease} />
    </>
  )
}

PressReleasePage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) =>
  getStaticPropsErrorHandler({ requestUrl: getUrlFromThisFilePath(params) }, async () => {
    const locale = getLocaleFromParams(params)
    const pressRelease = await getPressRelease(params?.slug as string, preview, locale)

    if (!pressRelease) {
      return {
        notFound: true,
        revalidate: Durations.FIVE_MINUTES_IN_SECONDS,
      }
    }

    return {
      props: {
        pressRelease,
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  })

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default PressReleasePage
